// @flow

import React from 'react';
import styled from 'styled-components';
import { colors, fonts } from 'shared/styleguide';
import { i18n } from 'shared/utils';
import { getCurrencySymbol } from 'shared/utils/currency';
import Icon from 'shared/components/common/Icon';

type VarianceType = 'high' | 'medium' | 'low';

type Props = {
  variance: number,
  targetCost: number,
  actualCost: number,
};

const AlertWrapper = styled.div`
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  background-color: ${p => {
    if (p.type === 'high') return '#FEE2E2';
    if (p.type === 'medium') return '#FEF3C7';
    return '#D1FAE5';
  }};
  border-left: 4px solid ${p => {
    if (p.type === 'high') return colors.cherry;
    if (p.type === 'medium') return colors.macaroniAndCheese;
    return colors.turquoise;
  }};
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${p => {
    if (p.type === 'high') return colors.cherry;
    if (p.type === 'medium') return colors.macaroniAndCheese;
    return colors.turquoise;
  }};
  color: ${colors.white};
  flex-shrink: 0;
`;

const Content = styled.div`
  flex: 1;
`;

const Title = styled.div`
  ${fonts.bodyBold};
  color: ${colors.charcoalGrey};
  margin-bottom: 4px;
`;

const Message = styled.div`
  ${fonts.bodyRegular};
  color: ${colors.battleshipGrey};
  line-height: 1.5;
`;

const VarianceValue = styled.span`
  ${fonts.bodyBold};
  color: ${p => {
    if (p.type === 'high') return colors.cherry;
    if (p.type === 'medium') return colors.rawSienna;
    return colors.jungleGreen;
  }};
`;

const CostComparison = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid ${p => {
    if (p.type === 'high') return '#FCA5A5';
    if (p.type === 'medium') return '#FCD34D';
    return '#6EE7B7';
  }};
`;

const CostItem = styled.div`
  ${fonts.smallRegular};
  color: ${colors.battleshipGrey};
  
  span {
    ${fonts.smallBold};
    color: ${colors.charcoalGrey};
  }
`;

const getVarianceType = (variance: number): VarianceType => {
  const absVariance = Math.abs(variance);
  if (absVariance > 15) return 'high';
  if (absVariance > 5) return 'medium';
  return 'low';
};

const getVarianceIcon = (type: VarianceType) => {
  if (type === 'high') return 'circle-alert-red';
  if (type === 'medium') return 'circle-info';
  return 'circle-check-green';
};

const CostVarianceAlert = ({ variance, targetCost, actualCost }: Props) => {
  const type = getVarianceType(variance);
  const icon = getVarianceIcon(type);
  const isOverBudget = variance > 0;

  const getTitle = () => {
    if (type === 'high') {
      return isOverBudget
        ? i18n.t('High Cost Variance Detected')
        : i18n.t('Significant Under Budget');
    }
    if (type === 'medium') {
      return isOverBudget
        ? i18n.t('Moderate Cost Variance')
        : i18n.t('Under Budget');
    }
    return i18n.t('Cost Within Target Range');
  };

  const getMessage = () => {
    if (type === 'high' && isOverBudget) {
      return i18n.t(
        'Current costs are significantly above target. Review component costs and labor estimates to identify savings opportunities.',
      );
    }
    if (type === 'high' && !isOverBudget) {
      return i18n.t(
        'Costs are well below target. Verify component quality and ensure all costs are properly accounted for.',
      );
    }
    if (type === 'medium' && isOverBudget) {
      return i18n.t(
        'Costs are moderately above target. Consider reviewing component suppliers or manufacturing processes.',
      );
    }
    if (type === 'medium' && !isOverBudget) {
      return i18n.t(
        'Costs are below target. Monitor to ensure quality standards are maintained.',
      );
    }
    return i18n.t(
      'Actual costs are within acceptable range of target costs. Continue monitoring for changes.',
    );
  };

  return (
    <AlertWrapper type={type}>
      <IconWrapper type={type}>
        <Icon type={icon} size={24} />
      </IconWrapper>
      <Content>
        <Title>{getTitle()}</Title>
        <Message>{getMessage()}</Message>
        <CostComparison type={type}>
          <CostItem>
            {i18n.t('Target')}: <span>{getCurrencySymbol()}{targetCost.toFixed(2)}</span>
          </CostItem>
          <CostItem>
            {i18n.t('Actual')}: <span>{getCurrencySymbol()}{actualCost.toFixed(2)}</span>
          </CostItem>
          <CostItem>
            {i18n.t('Variance')}: 
            <VarianceValue type={type}>
              {isOverBudget ? '+' : ''}{variance.toFixed(1)}%
            </VarianceValue>
          </CostItem>
        </CostComparison>
      </Content>
    </AlertWrapper>
  );
};

export default CostVarianceAlert;
